import React, { useState, useMemo, useEffect } from "react";

/**
 * USDT SEND (ON-CHAIN, CUSTODIAL)
 * - Fee-aware
 * - Minimum send enforced
 * - Preview / confirm screen before sending (mirrors BuyUswd's buy-confirm step)
 * - Success screen after send
 * - Live balance update via socket event
 * - Tailwind only
 *
 * NOTE: All transaction building, signing, and broadcasting now
 * happens entirely server-side (see socket.on("usdt:send")).
 * The frontend just sends { toAddress, amount } and waits for
 * usdt:send:success / usdt:send:error via onSendSuccess/onSendError.
 */

const BackChevron = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label="Back"
    className="flex items-center justify-center w-9 h-9 shrink-0 rounded-full
               text-neutral-700 dark:text-neutral-200
               hover:bg-neutral-100 dark:hover:bg-neutral-800
               active:opacity-60 touch-manipulation transition-colors"
  >
    <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
      <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
    </svg>
  </button>
);

export default function UsdtSend({
  wallet,
  usdtSocket,     // 🔥 socket functions  { sendUsdt, ... }
  usdtBalance,    // 🔥 { balance, committed, available }
  onBack,
  onSendSuccess,
  onSendError,
  mobileView,

}) {
  /* ================= CONSTANTS ================= */
  const USDT_MIN_SEND = 1;
  const USDT_SEND_FEE = 0.3;

  /* ================= STATE ================= */
  /*
   * view: "send-input" | "send-confirm" | "send-success"
   * Mirrors BuyUswd's view-based flow: fill the form, preview/confirm
   * the details, then land on a success screen once the socket
   * confirms the send.
   */
  const [view,           setView]           = useState("send-input");
  const [toAddress,      setToAddress]      = useState("");
  const [amount,         setAmount]         = useState("");
  const [sending,        setSending]        = useState(false);
  const [error,          setError]          = useState("");
  const [txInfo,         setTxInfo]         = useState(null);    // ← { txSig, amount, fee }

  /*
   * Local copy of available balance so we can update it
   * immediately when the socket fires usdt:balance without
   * needing the parent to re-render first.
   */
  const [localAvailable, setLocalAvailable] = useState(
    Number(usdtBalance?.available ?? 0)
  );

  // Keep in sync if parent prop changes (e.g. parent fetched fresh data)
  useEffect(() => {
    setLocalAvailable(Number(usdtBalance?.available ?? 0));
  }, [usdtBalance?.available]);

  useEffect(() => {

    if (!onSendSuccess || !onSendError) return;

    onSendSuccess.current = (payload) => {

      setSending(false);
      setTxInfo(payload);
      setView("send-success");

    };

    onSendError.current = (payload) => {

      setSending(false);

      setError(
        payload?.message ||
        "USDT send failed"
      );

    };

    return () => {

      onSendSuccess.current = null;
      onSendError.current = null;

    };

  }, [onSendSuccess, onSendError]);

  /* ================= MONEY (CENTS) ================= */
  const toCents = (v) => Math.round(Number(v) * 100);

  const availableCents = toCents(localAvailable);
  const numericAmount  = Number(amount);
  const amountCents    = toCents(numericAmount);
  const feeCents       = toCents(USDT_SEND_FEE);

  /* ================= DERIVED (CENT-SAFE) ================= */
  const totalDebitCents = useMemo(() => {
    if (!numericAmount || numericAmount <= 0) return 0;
    return amountCents + feeCents;
  }, [amountCents, feeCents, numericAmount]);

  const totalDebit = useMemo(
    () => (totalDebitCents / 100).toFixed(2),
    [totalDebitCents]
  );

  const trimmedAddress = toAddress.trim();

  const canPreview =
    amountCents >= toCents(USDT_MIN_SEND) &&
    amountCents + feeCents <= availableCents &&
    Boolean(trimmedAddress);

  /* ================= GUARD ================= */
  if (!wallet?.chatId) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="rounded-lg bg-neutral-100 px-6 py-4 dark:bg-neutral-800">
          Loading USDT wallet…
        </div>
      </div>
    );
  }


  /* ================= HANDLERS ================= */
  const handleMax = () => {
    if (localAvailable > USDT_SEND_FEE) {
      setAmount((localAvailable - USDT_SEND_FEE).toFixed(2));
    }
  };

  const validate = () => {
    if (!trimmedAddress) {
      setError("Recipient Solana address is required");
      return false;
    }

    if (!Number.isFinite(numericAmount)) {
      setError("Invalid USDT amount");
      return false;
    }

    if (numericAmount < USDT_MIN_SEND) {
      setError(`Minimum send is ${USDT_MIN_SEND} USDT`);
      return false;
    }

    if (amountCents + feeCents > availableCents) {
      setError("Insufficient available USDT balance");
      return false;
    }

    // Basic client-side sanity check only — the backend is the
    // source of truth for address validity (assertValidSolanaAddress)
    // and amount rules (assertMinUsdtSend), since it now builds,
    // signs, and broadcasts the entire transaction itself.
    if (trimmedAddress.length < 32 || trimmedAddress.length > 44) {
      setError("Invalid recipient Solana address");
      return false;
    }

    return true;
  };

  const handlePreview = () => {
    setError("");
    if (!validate()) return;
    setView("send-confirm");
  };

  const handleBackToInput = () => {
    setError("");
    setView("send-input");
  };

  const handleConfirmSend = () => {
    setError("");

    // Re-validate in case the balance or inputs shifted while reviewing.
    if (!validate()) {
      setView("send-input");
      return;
    }

    setSending(true);

    /*
     * Nothing else to build client-side: the backend decrypts the
     * sender's custodial key, resolves ATAs, builds both transfer
     * instructions (recipient + platform fee), signs, and broadcasts.
     * `sending` is cleared by the onSendSuccess/onSendError socket
     * listeners registered above — never by a timeout here, to avoid
     * a race condition on slow networks.
     */
    usdtSocket.sendUsdt({
      toAddress: trimmedAddress,
      amount: numericAmount,
    });
  };

  const handleSendAnother = () => {
    setView("send-input");
    setTxInfo(null);
    setToAddress("");
    setAmount("");
    setError("");
  };

  /* ============================================================
     VIEW: send-success
  ============================================================ */
  if (view === "send-success") {
    return (
      <div className="flex h-full w-full flex-col bg-white dark:bg-neutral-900">
        {/* Header */}
        <div className="border-b border-neutral-200 p-4 dark:border-neutral-700">
          <div className="flex items-center gap-3">
            {onBack && <BackChevron onClick={onBack} />}
            <h2 className="text-base font-semibold text-orange-400">
              Send USDT (Solana)
            </h2>
          </div>
        </div>

        {/* Success body */}
        <div className="flex flex-1 flex-col items-center justify-center gap-6 px-6 py-8 text-center">
          {/* Checkmark icon */}
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
            <svg
              className="h-10 w-10 text-green-600 dark:text-green-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.5}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>

          <div>
            <p className="text-xl font-bold text-neutral-900 dark:text-white">
              USDT Sent!
            </p>
            <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
              Your transaction was submitted successfully.
            </p>
          </div>

          {/* Transaction details */}
          <div className="w-full rounded-xl border border-neutral-200 bg-neutral-50 p-4 text-left text-sm dark:border-neutral-700 dark:bg-neutral-800">
            {txInfo?.amount !== undefined && (
              <div className="flex justify-between py-1">
                <span className="text-neutral-500 dark:text-neutral-400">Amount sent</span>
                <strong className="text-neutral-900 dark:text-white">
                  {Number(txInfo.amount).toFixed(2)} USDT
                </strong>
              </div>
            )}
            {txInfo?.fee !== undefined && (
              <div className="flex justify-between py-1">
                <span className="text-neutral-500 dark:text-neutral-400">Network fee</span>
                <strong className="text-neutral-900 dark:text-white">
                  {Number(txInfo.fee).toFixed(2)} USDT
                </strong>
              </div>
            )}
            <div className="flex justify-between py-1">
              <span className="text-neutral-500 dark:text-neutral-400">New balance</span>
              <strong className="text-green-600 dark:text-green-400">
                {localAvailable.toFixed(2)} USDT
              </strong>
            </div>
            {txInfo?.txSig && (
              <div className="mt-2 border-t border-neutral-200 pt-2 dark:border-neutral-700">
                <p className="text-xs text-neutral-500 dark:text-neutral-400">Transaction ID</p>
                <p className="mt-0.5 break-all text-xs font-mono text-neutral-700 dark:text-neutral-300">
                  {txInfo.txSig}
                </p>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex w-full flex-col gap-3">
            <button
              onClick={handleSendAnother}
              className="
                w-full rounded-lg bg-orange-400 px-4 py-2.5
                text-sm font-semibold text-white
                hover:bg-orange-500
              "
            >
              Send Another
            </button>
            <button
              onClick={onBack}
              className="
                w-full rounded-lg border px-4 py-2.5
                text-sm font-semibold
                border-neutral-300 text-neutral-700
                hover:bg-neutral-100
                dark:border-neutral-600 dark:text-neutral-200 dark:hover:bg-neutral-800
              "
            >
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ============================================================
     VIEW: send-confirm
  ============================================================ */
  if (view === "send-confirm") {
    const afterSend = Number((localAvailable - Number(totalDebit)).toFixed(2));

    return (
      <div className="flex h-full w-full flex-col bg-white dark:bg-neutral-900">
        {/* Header */}
        <div className="border-b border-neutral-200 p-4 dark:border-neutral-700">
          <div className="flex items-center gap-3">
            <BackChevron onClick={handleBackToInput} />
            <h2 className="text-base font-semibold text-orange-400">
              Confirm Send
            </h2>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-4">
          <p className="text-sm text-neutral-500 dark:text-neutral-400">
            Please review the details below before confirming.
          </p>

          <div className="rounded-xl border bg-neutral-50 dark:border-neutral-700
                          dark:bg-neutral-800 divide-y dark:divide-neutral-700">
            <div className="flex justify-between gap-4 px-4 py-3 text-sm">
              <span className="shrink-0 text-neutral-500 dark:text-neutral-400">Recipient</span>
              <span className="break-all text-right font-mono text-xs text-neutral-700 dark:text-neutral-200">
                {trimmedAddress}
              </span>
            </div>
            <div className="flex justify-between px-4 py-3 text-sm">
              <span className="text-neutral-500 dark:text-neutral-400">Amount</span>
              <strong className="text-orange-500">{numericAmount.toFixed(2)} USDT</strong>
            </div>
            <div className="flex justify-between px-4 py-3 text-sm">
              <span className="text-neutral-500 dark:text-neutral-400">Network fee</span>
              <span className="text-neutral-600 dark:text-neutral-300">
                {USDT_SEND_FEE.toFixed(2)} USDT
              </span>
            </div>
            <div className="flex justify-between px-4 py-3 text-sm font-semibold">
              <span className="text-neutral-700 dark:text-neutral-200">Total to deduct</span>
              <span className="text-orange-500">{totalDebit} USDT</span>
            </div>
            <div className="flex justify-between px-4 py-3 text-sm">
              <span className="text-neutral-500 dark:text-neutral-400">USDT balance after</span>
              <span className="font-semibold text-neutral-900 dark:text-white">
                {afterSend.toFixed(2)} USDT
              </span>
            </div>
          </div>

          <div className="rounded-xl bg-amber-50 border border-amber-200
                          dark:bg-amber-950 dark:border-amber-800 px-4 py-3">
            <p className="text-xs text-amber-700 dark:text-amber-300">
              This action cannot be undone. Double-check the recipient address before confirming.
            </p>
          </div>

          {error && (
            <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600 dark:bg-red-900/20 dark:text-red-400">
              {error}
            </div>
          )}
        </div>

        <div className="border-t border-neutral-200 px-6 py-4 dark:border-neutral-700 flex gap-3">
          <button
            onClick={handleBackToInput}
            disabled={sending}
            className="flex-1 rounded-lg border py-2.5 text-sm font-semibold
                       dark:border-neutral-600 hover:bg-neutral-50
                       dark:hover:bg-neutral-800 transition-colors disabled:opacity-40"
          >
            Edit
          </button>
          <button
            onClick={handleConfirmSend}
            disabled={sending}
            className="flex-1 flex items-center justify-center gap-2 rounded-lg bg-orange-400
                       py-2.5 text-sm font-semibold text-white disabled:opacity-40
                       hover:bg-orange-500 transition-colors"
          >
            {sending ? (
              <>
                <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg>
                Sending…
              </>
            ) : (
              "Confirm & Send"
            )}
          </button>
        </div>
      </div>
    );
  }

  /* ============================================================
     VIEW: send-input
  ============================================================ */
  return (
    <div className="flex h-full w-full flex-col bg-white dark:bg-neutral-900">
      {/* Header */}
      <div className="border-b border-neutral-200 p-4 dark:border-neutral-700">
        <div className="flex items-center gap-3">
          {onBack && <BackChevron onClick={onBack} />}
          <h2 className="text-base font-semibold text-orange-400">
            Send USDT (Solana)
          </h2>
        </div>
      </div>

      {/* Content */}
      <div className="flex flex-col gap-6 p-6">

        {/* Balance */}
        <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-4 dark:border-neutral-700 dark:bg-neutral-800">
          <div className="text-xs text-neutral-500 dark:text-neutral-400">
            Available Balance
          </div>
          <div className="mt-1 text-xl font-bold text-green-600 dark:text-green-400">
            {localAvailable.toFixed(2)}{" "}
            <span className="text-sm font-medium text-neutral-500 dark:text-neutral-400">
              USDT
            </span>
          </div>
        </div>

        {/* Form */}
        <div className="flex flex-col gap-4">

          {/* Address */}
          <div>
            <label className="mb-1 block text-xs font-semibold text-neutral-700 dark:text-white">
              Recipient Solana Address
            </label>
            <input
              value={toAddress}
              onChange={(e) => setToAddress(e.target.value)}
              placeholder="Solana address"
              className="
                w-full rounded-lg border px-4 py-2.5 text-sm
                border-neutral-300 bg-white text-neutral-900
                focus:outline-none focus:ring-2 focus:ring-orange-400
                dark:border-neutral-600 dark:bg-neutral-900 dark:text-white
              "
            />
          </div>

          {/* Amount + MAX */}
          <div>
            <label className="mb-1 block text-xs font-semibold text-neutral-700 dark:text-white">
              Amount (USDT)
            </label>
            <div className="relative">
              <input
                type="number"
                min={USDT_MIN_SEND}
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                className="
                  w-full rounded-lg border px-4 py-2.5 pr-16 text-sm
                  border-neutral-300 bg-white text-neutral-900
                  focus:outline-none focus:ring-2 focus:ring-green-500
                  dark:border-neutral-600 dark:bg-neutral-900 dark:text-white
                "
              />
              <button
                type="button"
                onClick={handleMax}
                className="
                  absolute right-2 top-1/2 -translate-y-1/2
                  rounded-md px-2 py-1 text-xs font-semibold
                  text-green-600 hover:bg-green-50
                  dark:text-green-400 dark:hover:bg-neutral-700
                "
              >
                MAX
              </button>
            </div>
          </div>

          {/* Info */}
          <div className="rounded-lg bg-neutral-50 p-3 text-xs dark:bg-neutral-800">
            <div>Minimum send: <strong>{USDT_MIN_SEND} USDT</strong></div>
            <div>Network fee:  <strong>{USDT_SEND_FEE} USDT</strong></div>
            {numericAmount >= USDT_MIN_SEND && (
              <div className="mt-1 text-green-600 dark:text-green-400">
                Total deducted: <strong>{totalDebit} USDT</strong>
              </div>
            )}
          </div>

          {error && (
            <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600 dark:bg-red-900/20 dark:text-red-400">
              {error}
            </div>
          )}

          {/* Preview button */}
          <button
            disabled={!canPreview}
            onClick={handlePreview}
            className="
              mt-2 flex items-center justify-center
              rounded-lg px-4 py-2.5 text-sm font-semibold
              bg-orange-400 text-white
              hover:bg-orange-500
              disabled:opacity-50 disabled:cursor-not-allowed
            "
          >
            {numericAmount >= USDT_MIN_SEND
              ? `Preview: Send ${numericAmount.toFixed(2)} USDT`
              : "Send USDT"}
          </button>
        </div>

        {/* Footnote */}
        <div className="text-xs text-neutral-500 dark:text-neutral-400">
          • Send only <strong>USDT (SPL)</strong> on Solana<br />
          • Network fees are deducted automatically
        </div>
      </div>
    </div>
  );
}